import { useMemo } from 'react';
import { useCookie } from 'react-use';

type CookieQueueKey = 'PRODUCTS' | 'BRANDS';

// Constants
const DEFAULT_MAX_QUEUE_SIZE = 30;
const USE_COOKIES_QUEUE_KEYS: Record<CookieQueueKey, string> = {
  PRODUCTS: 'recently-browsed-products-queue',
  BRANDS: 'recently-browsed-brands-queue',
  // Add more keys as needed
};

interface AddToQueueProps {
  id: string;
  name: string;
}

interface QueueItem extends AddToQueueProps {
  timestamp: string;
}

const useCookieQueue = (
  key: string,
  maxQueueSize: number = DEFAULT_MAX_QUEUE_SIZE
): { queue: QueueItem[]; addToCookiesQueue: (data: AddToQueueProps) => void } => {
  const [rawQueue, setRawQueue] = useCookie(key);

  const parsedQueue: QueueItem[] = useMemo(() => {
    try {
      return rawQueue ? JSON.parse(rawQueue) : [];
    } catch {
      return [];
    }
  }, [rawQueue]);

  const addToCookiesQueue = (data: AddToQueueProps) => {
    if (!data || !data?.id) {
      console.error('New object must have an id.');
      return;
    }

    const queue = [...parsedQueue];

    const newEntry = { ...data, timestamp: new Date().toISOString() };

    const existingIndex = queue.findIndex((item) => item.id === data.id);

    if (existingIndex !== -1) {
      queue.splice(existingIndex, 1); // Remove the existing object
    }

    queue.unshift(newEntry);

    if (queue.length > maxQueueSize) {
      queue.pop(); // Remove the last element if the queue exceeds max size
    }

    setRawQueue(JSON.stringify(queue));
  };

  return { queue: JSON.parse(rawQueue), addToCookiesQueue };
};

export { useCookieQueue, USE_COOKIES_QUEUE_KEYS };
